/**
 * BankFeedHeader - Phase 3 cont.
 *
 * Page header with title, back-button, cache indicator, background-jobs
 * progress, Upload / Transfer buttons, and a hidden file input.
 *
 * The component is purely presentational; the parent owns state and
 * handlers and passes them in as props.
 */
import React from 'react';
import { Button } from '../ui/button';
import {
  ArrowLeftRight,
  Brain,
  Loader2,
  Upload,
} from 'lucide-react';
import { CacheIndicator } from '../CacheIndicator';
import PeriodLockChip from './PeriodLockChip';

export const BankFeedHeader = ({
  loading,
  uploading,
  uploadProgress,
  fileInputRef,
  onFileUpload,
  onOpenUploadStarter,
  onOpenTransfer,
  onRefreshCache,
}) => {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
      <div>
        <div className="flex items-start justify-between w-full gap-2 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-[#108981] shrink-0" />
              AI Bank Transactions Center
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-gray-500 text-sm">
                Intelligent processing of bank transactions from all sources
              </p>
              <PeriodLockChip compact />
            </div>
          </div>
          <CacheIndicator cacheKey="bankfeed_stats" onRefresh={onRefreshCache} loading={loading} />
        </div>

        {/* iter1234-hotfix - The per-page BackgroundJobsIndicator card
            was previously rendered here in parallel to the global
            GlobalUploadProgressBadge banner (mounted in DashboardLayout).
            The two showed the same filename, %, and status - a redundant
            duplication users complained about. The global banner is the
            single source of truth now; Cancel + Review actions were
            moved into it. Awaiting-confirmation / backlog-fee jobs are
            still handled by GlobalUploadBacklogModalProvider. */}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <div className="relative">
          {/* Hidden file input owned by the header. The visible CTA now
              opens the UploadStarterDialog first (guide + limits + template
              downloads). When the user clicks "Select statement file" inside
              the starter, it calls `fileInputRef.current.click()` which
              fires this input's onChange → the existing handleFileSelect
              flow (bank-account picker + upload) is untouched. */}
          <input
            type="file"
            id="statement-file-input"
            ref={fileInputRef}
            onChange={(e) => {
              onFileUpload(e);
              // Allow re-selecting the same file after an error.
              try { e.target.value = ""; } catch (_) { /* noop */ }
            }}
            accept=".csv,.txt,.pdf,.xlsx,.xls,.ofx,.qfx"
            multiple
            data-testid="statement-file-input"
            className="sr-only"
            disabled={uploading}
          />
          <button
            type="button"
            onClick={onOpenUploadStarter}
            disabled={uploading}
            data-testid="upload-statement-btn"
            aria-disabled={uploading}
            className={
              "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white " +
              (uploading
                ? "bg-[#108981] opacity-60 cursor-not-allowed pointer-events-none"
                : "bg-[#108981] hover:bg-[#0a6b65] cursor-pointer")
            }
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Statement
              </>
            )}
          </button>
          <Button
            variant="outline"
            onClick={onOpenTransfer}
            className="gap-2 ml-2"
            title="Record money you moved between your own bank accounts"
            data-testid="transfer-between-banks-btn"
          >
            <ArrowLeftRight className="w-4 h-4" />
            <span>Transfer<span className="hidden md:inline"> between banks</span></span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BankFeedHeader;
